import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"
import { Calendar } from "fullcalendar"
import interactionPlugin from "fullcalendar/interaction"
import timeGridPlugin from "fullcalendar/timegrid"
import themePlugin from "fullcalendar/themes/classic"

export default class extends Controller {
  static values = {
    weekStartDay: { type: Number, default: 1 }
  }

  static targets = [
    "calendar",
    "eventsData",
    "form",
    "methodInput",
    "weekdayInput",
    "startTimeInput",
    "endTimeInput",
    "applyAllWeekdaysInput",
    "reasonInput",
    "commentInput",
    "deleteForm"
  ]

  connect() {
    this.events = this.parseJson(this.eventsDataTarget)

    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [themePlugin, interactionPlugin, timeGridPlugin],
      initialView: "timeGridWeek",
      initialDate: this.startOfCurrentWeek(),
      firstDay: this.weekStartDayValue,
      allDaySlot: false,
      height: "auto",
      selectable: true,
      slotDuration: "00:15:00",
      snapDuration: "00:15:00",
      slotLabelInterval: "01:00:00",
      slotMinTime: "00:00:00",
      slotMaxTime: "24:00:00",
      dayHeaderContent: (arg) => this.dayHeaderContent(arg),
      headerToolbar: {
        left: "",
        center: "",
        right: ""
      },
      events: this.events,
      select: (selectionInfo) => this.openDialog({
        weekday: this.mondayWeekday(selectionInfo.start),
        startTime: this.timeString(selectionInfo.start),
        endTime: this.selectionEndTimeString(selectionInfo.start, selectionInfo.end),
        reason: "No venue access",
        comment: ""
      }),
      eventClick: (clickInfo) => {
        const props = clickInfo.event.extendedProps
        this.openDialog({
          recordId: props.recordId,
          weekday: props.weekday,
          startTime: props.startTime.slice(0, 5),
          endTime: props.endTime.slice(0, 5),
          reason: props.reason || "No venue access",
          comment: props.comment || ""
        }, true)
      }
    })

    this.calendar.render()
  }

  disconnect() {
    this.calendar?.destroy()
  }

  async openDialog(payload, editing = false) {
    const timeOptions = this.timeOptionsHtml()

    const result = await Swal.fire({
      title: editing ? "Edit weekly no-access restriction" : "New weekly no-access restriction",
      html: `
        <div class="grid gap-3 text-left">
          <label class="text-sm font-semibold text-stone-700">Weekday</label>
          <select id="swal-weekday" class="swal2-input !mt-0">
            ${this.weekdayOptionsHtml(payload.weekday)}
          </select>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-sm font-semibold text-stone-700">Start</label>
              <select id="swal-start" class="swal2-input !mt-0" style="width: 9rem; min-width: 9rem; padding-right: 2rem;">
                ${timeOptions}
              </select>
            </div>
            <div>
              <label class="text-sm font-semibold text-stone-700">End</label>
              <select id="swal-end" class="swal2-input !mt-0" style="width: 9rem; min-width: 9rem; padding-right: 2rem;">
                ${timeOptions}
              </select>
            </div>
          </div>
          <label class="text-sm font-semibold text-stone-700">Reason</label>
          <input id="swal-reason" class="swal2-input !mt-0" value="${this.escapeHtml(payload.reason)}">
          <label class="text-sm font-semibold text-stone-700">Optional comment</label>
          <textarea id="swal-comment" class="swal2-textarea !mt-0" placeholder="Optional note">${this.escapeHtml(payload.comment)}</textarea>
          ${editing ? "" : `
            <label class="mt-1 flex items-start gap-3 rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm text-stone-700">
              <input id="swal-apply-all-weekdays" type="checkbox" class="mt-1 h-4 w-4 rounded border-stone-300 text-stone-900" checked>
              <span>
                <span class="block font-semibold">Apply the same closed hours to every day of the week</span>
                <span class="mt-1 block text-xs leading-5 text-stone-500">Untick this to create the restriction for the selected weekday only.</span>
              </span>
            </label>
          `}
        </div>
      `,
      showCancelButton: true,
      showDenyButton: editing,
      confirmButtonText: editing ? "Save changes" : "Create restriction",
      denyButtonText: "Delete",
      cancelButtonText: "Cancel",
      focusConfirm: false,
      width: 640,
      didOpen: () => {
        document.getElementById("swal-start").value = payload.startTime
        document.getElementById("swal-end").value = payload.endTime
      },
      preConfirm: () => {
        const weekday = document.getElementById("swal-weekday").value
        const startTime = document.getElementById("swal-start").value
        const endTime = document.getElementById("swal-end").value
        const reason = document.getElementById("swal-reason").value
        const comment = document.getElementById("swal-comment").value
        const applyAllWeekdays = !editing && document.getElementById("swal-apply-all-weekdays")?.checked

        if (!startTime || !endTime || !reason) {
          Swal.showValidationMessage("Start, end, and reason are required.")
          return false
        }

        if (endTime <= startTime) {
          Swal.showValidationMessage("End time must be after the start time.")
          return false
        }

        return { weekday, startTime, endTime, reason, comment, applyAllWeekdays }
      }
    })

    if (result.isDenied && editing) {
      this.deleteFormTarget.action = `${this.deleteFormTarget.dataset.urlBase}${payload.recordId}`
      this.deleteFormTarget.requestSubmit()
      return
    }

    if (!result.isConfirmed) return

    const values = result.value
    this.formTarget.action = editing ? `${this.formTarget.dataset.updateUrlBase}${payload.recordId}` : this.formTarget.dataset.createUrl
    this.methodInputTarget.value = editing ? "patch" : "post"
    this.weekdayInputTarget.value = values.weekday
    this.startTimeInputTarget.value = values.startTime
    this.endTimeInputTarget.value = values.endTime
    if (this.hasApplyAllWeekdaysInputTarget) {
      this.applyAllWeekdaysInputTarget.value = values.applyAllWeekdays ? "1" : "0"
    }
    this.reasonInputTarget.value = values.reason
    this.commentInputTarget.value = values.comment
    this.formTarget.requestSubmit()
  }

  weekdayOptionsHtml(selectedValue) {
    return ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
      .map((name, index) => `<option value="${index}" ${String(index) === String(selectedValue) ? "selected" : ""}>${name}</option>`)
      .join("")
  }

  timeOptionsHtml() {
    const options = []

    for (let minute = 0; minute <= 24 * 60; minute += 15) {
      const hours = Math.floor(minute / 60)
      const remainder = minute % 60
      const label = `${String(hours).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
      options.push(`<option value="${label}">${label}</option>`)
    }

    return options.join("")
  }

  parseJson(element) {
    try {
      return JSON.parse(element.textContent || "[]")
    } catch (_error) {
      return []
    }
  }

  timeString(date) {
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
  }

  selectionEndTimeString(startDate, endDate) {
    if (
      endDate.getHours() === 0 &&
      endDate.getMinutes() === 0 &&
      endDate.getTime() > startDate.getTime()
    ) {
      return "24:00"
    }

    return this.timeString(endDate)
  }

  mondayWeekday(date) {
    return (date.getDay() + 6) % 7
  }

  startOfCurrentWeek() {
    const today = new Date()
    const clone = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    clone.setDate(clone.getDate() - ((clone.getDay() - this.weekStartDayValue + 7) % 7))
    return clone
  }

  dayHeaderContent(arg) {
    const weekday = new Intl.DateTimeFormat(undefined, { weekday: "long" }).format(arg.date)

    return {
      html: `
        <div class="flex flex-col items-center">
          <span>${this.escapeHtml(weekday)}</span>
        </div>
      `
    }
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;")
      .replaceAll("'", "&#39;")
  }
}
